import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, InputNumber, Button, Typography, Alert, Table, Divider } from 'antd';
import { PointOfSaleTransaction } from '../../model/pointofsale/PointOfSaleTransaction';
import { UpdatePaymentTransactionRequest } from '../../model/pointofsale/UpdatePaymentTransactionRequest';
import { usePointOfSaleService } from '../../services/pointofsale/PointOfSaleServiceProvider';
import { useMemberService } from '../../services/members/MemberServiceProvider';
import dayjs from 'dayjs';
import { useFormatCurrency, useCurrencySymbol } from '../../hooks/useFormatters';

const { Text, Title } = Typography;
const { Option } = Select;

interface UpdateTransactionModalProps {
    visible: boolean;
    transaction: PointOfSaleTransaction | null;
    onCancel: () => void;
    onSuccess: () => void;
}

const UpdateTransactionModal: React.FC<UpdateTransactionModalProps> = ({
    visible,
    transaction,
    onCancel,
    onSuccess
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [memberName, setMemberName] = useState<string>('');
    const [error, setError] = useState<string>('');
    
    const pointOfSaleService = usePointOfSaleService();
    const memberService = useMemberService();
    const formatCurrency = useFormatCurrency();
    const currencySymbol = useCurrencySymbol();

    // Fetch member name when transaction changes
    useEffect(() => {
        if (transaction && visible) {
            try {
                memberService.getMember(transaction.membershipId).then(member => {
                    setMemberName(member?.name || `Unknown (${transaction.membershipId})`);
                });
            } catch (error) {
                console.error('Error fetching member name:', error);
                setMemberName(`Unknown (${transaction.membershipId})`);
            }
        }
    }, [transaction, visible, memberService]);

    // Reset form when modal opens/closes
    useEffect(() => {
        if (visible && transaction) {            
            form.setFieldsValue({
                transactionId: transaction.transactionId,
                status: transaction.status,
                amount: transaction.amount
            });
            setError('');
        } else if (!visible) {
            form.resetFields();
            setError('');
        }
    }, [visible, transaction, form]);

    const handleSubmit = async () => {
        try {
            setLoading(true);
            setError('');

            const values = await form.validateFields();
            
            const request: UpdatePaymentTransactionRequest = {
                transactionId: parseInt(values.transactionId),
                status: values.status,
                amount: values.amount,
                items: transaction?.items || [],
                bookingSlots: transaction?.bookingSlots || []
            };

            console.log('Sending update request:', request);
            await pointOfSaleService.updateTransaction(request);
            
            onSuccess();
            onCancel();
        } catch (error: any) {
            console.error('Error updating transaction:', error);
            setError(error.message || 'Failed to update transaction. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setError('');
        onCancel();
    };

    if (!transaction) return null;

    // Define columns for items table
    const itemColumns = [
        {
            title: 'Item Name',
            dataIndex: 'item',
            key: 'itemName',
            render: (item: any) => item.name,
            width: 200
        },
        {
            title: 'Type',
            dataIndex: 'item',
            key: 'type',
            render: (item: any) => item.type,
            width: 120
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
            width: 80,
            align: 'right' as const
        },
        {
            title: 'Unit Price',
            dataIndex: 'item',
            key: 'unitPrice',
            render: (item: any) => formatCurrency(item.price),
            width: 100,
            align: 'right' as const
        },
        {
            title: 'Total',
            key: 'total',
            render: (record: any) => formatCurrency(record.item.price * record.quantity),
            width: 100,
            align: 'right' as const
        }
    ];

    // Define columns for booking slots table
    const bookingColumns = [
        {
            title: 'Facility',
            dataIndex: 'bookingSlot',
            key: 'facilityName',
            render: (bookingSlot: any) => bookingSlot.facilityName,
            width: 150
        },
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
            width: 100
        },
        {
            title: 'Start Time',
            dataIndex: 'bookingSlot',
            key: 'startTime',
            render: (bookingSlot: any) => dayjs(bookingSlot.startTime).format('HH:mm'),
            width: 100
        },
        {
            title: 'End Time',
            dataIndex: 'bookingSlot',
            key: 'endTime',
            render: (bookingSlot: any) => dayjs(bookingSlot.endTime).format('HH:mm'),
            width: 100
        }
    ];

    return (
        <Modal
            title="Update Transaction"
            open={visible}
            onCancel={handleCancel}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    Cancel
                </Button>,
                <Button 
                    key="submit" 
                    data-testid="update-transaction-submit"
                    type="primary" 
                    loading={loading}
                    onClick={handleSubmit}
                >
                    Update Transaction
                </Button>
            ]}
            width={900}
        >
            {error && (
                <Alert
                    message="Error"
                    description={error}
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    transactionId: transaction.transactionId,
                    status: transaction.status,
                    amount: transaction.amount
                }}
            >
                {/* Display Fields (Read-only) */}
                <Form.Item label="Member">
                    <Input 
                        value={memberName} 
                        readOnly 
                        style={{ backgroundColor: '#f5f5f5' }}
                    />
                </Form.Item>

                <Form.Item label="Transaction Time">
                    <Input 
                        value={dayjs(transaction.timestamp).format('DD/MM/YYYY HH:mm:ss')} 
                        readOnly 
                        style={{ backgroundColor: '#f5f5f5' }}
                    />
                </Form.Item>

                <Form.Item label="Client">
                    <Input 
                        value={transaction.clientName} 
                        readOnly 
                        style={{ backgroundColor: '#f5f5f5' }}
                    />
                </Form.Item>

                {/* Editable Fields */}
                <Form.Item
                    name="transactionId"
                    label="Transaction ID"
                    rules={[
                        { required: true, message: 'Transaction ID is required' },
                        { type: 'number', min: 1, message: 'Transaction ID must be a positive number' }
                    ]}
                >
                    <InputNumber
                        inputMode="numeric"
                        style={{ width: '100%' }}
                        placeholder="Enter transaction ID"
                        min={1}
                        disabled
                    />
                </Form.Item>

                <Form.Item
                    name="status"
                    label="Status"
                    rules={[{ required: true, message: 'Status is required' }]}
                >
                    <Select placeholder="Select status">
                        <Option value="Completed">Completed</Option>
                        <Option value="Pending">Pending</Option>
                        <Option value="Failed">Failed</Option>
                        <Option value="Unpaid">Unpaid</Option>
                    </Select>
                </Form.Item>

                <Form.Item
                    name="amount"
                    label={`Amount (${currencySymbol})`}
                    rules={[
                        { required: true, message: 'Amount is required' },
                        { type: 'number', min: 0, message: 'Amount must be a positive number' }
                    ]}
                >
                    <InputNumber
                        inputMode="decimal"
                        style={{ width: '100%' }}
                        placeholder="Enter amount"
                        min={0}
                        step={0.01}
                        precision={2}
                        addonBefore={currencySymbol}
                    />
                </Form.Item>

                <Divider />

                {/* Individual Items Details */}
                {transaction.items.length > 0 && (
                    <div style={{ marginBottom: 24 }}>
                        <Title level={5}>Individual Items ({transaction.items.length})</Title>
                        <Table
                            columns={itemColumns}
                            dataSource={transaction.items}
                            pagination={false}
                            size="small"
                            scroll={{ x: 600 }}
                            rowKey={(record, index) => `item-${index}`}
                        />
                    </div>
                )}

                {/* Facility Bookings Details */}
                {transaction.bookingSlots.length > 0 && (
                    <div style={{ marginBottom: 24 }}>
                        <Title level={5}>Facility Bookings ({transaction.bookingSlots.length})</Title>
                        <Table
                            columns={bookingColumns}
                            dataSource={transaction.bookingSlots}
                            pagination={false}
                            size="small"
                            scroll={{ x: 600 }}
                            rowKey={(record, index) => `booking-${index}`}
                        />
                    </div>
                )}

                {/* Transaction Summary */}
                <div style={{ 
                    backgroundColor: '#f9f9f9', 
                    padding: '12px', 
                    borderRadius: '6px',
                    marginTop: '16px'
                }}>
                    <Text strong>Transaction Summary:</Text>
                    <div style={{ marginTop: '8px' }}>
                        <Text type="secondary">
                            Items: {transaction.items.length} • 
                            Bookings: {transaction.bookingSlots.length} • 
                            Original Amount: {formatCurrency(transaction.amount)}
                        </Text>
                    </div>
                </div>
            </Form>
        </Modal>
    );
};

export default UpdateTransactionModal; 